import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight } from "lucide-react";

const FAQ_IMAGE = "/faq-support.jpg";

const FAQS = [
  {
    q: "What can I actually hold inside one Tharwy account?",
    a: "Global and GCC shares, ETFs, commodities, forex, crypto, Shariah-screened portfolios, Murabaha plans and a USD savings balance — all under a single login, with one consolidated view of your net worth.",
  },
  {
    q: "Do I have to choose between halal and conventional investing?",
    a: "No. Every instrument carries a live Shariah screening status, and you can run a fully halal portfolio, a conventional one, or both side by side. Purification estimates are shown so you always know where you stand.",
  },
  {
    q: "Who holds my money, and what happens if I want it back?",
    a: "Cash sits in your multi-currency wallet and securities are held in your name with our execution partners. Withdrawals go back to the account you funded from — there is no lock-in, no notice period and no exit fee.",
  },
  {
    q: "What does Tharwy cost me?",
    a: "A flat 0.10% per trade and a management fee on managed portfolios only. No account minimum, no monthly platform fee, no inactivity charge, and currency conversion at a transparent live rate.",
  },
  {
    q: "Which currencies can I fund and hold?",
    a: "USD plus your home currency are opened automatically at signup, and you can add QAR, SAR, AED, EUR, GBP and a dozen more. Convert between them whenever you like — you are never forced to hold only dollars.",
  },
  {
    q: "How long does verification take before I can trade?",
    a: "Most identity checks clear in a few minutes once your ID and selfie are submitted. You can browse markets, build a portfolio and set up a plan while the check runs, and we email you the moment you're approved.",
  },
  {
    q: "Can I invest a fixed amount every month automatically?",
    a: "Yes. Pick a portfolio, an amount and a payday, and Tharwy quietly buys in for you each month. If your wallet is short we skip the run and tell you rather than selling anything or overdrawing.",
  },
  {
    q: "What is the difference between a managed portfolio and trading myself?",
    a: "A managed portfolio is built from your risk answers and rebalanced for you. Self-directed trading puts you in control of individual names, with order tickets, price alerts and a full blotter. Most clients use both.",
  },
];

const VISIBLE = 4;

export const FaqSection = ({ onCta }) => {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? FAQS : FAQS.slice(0, VISIBLE);

  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-24" data-testid="faq-section">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#3E2E65]/60">Good to know</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="faq-title">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {shown.map((f, i) => (
              <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-black/10">
                <AccordionTrigger className="text-start text-base sm:text-lg font-medium hover:no-underline py-5"
                  data-testid={`faq-question-${i}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#1A1330]/70 text-base leading-relaxed pb-5 pe-6"
                  data-testid={`faq-answer-${i}`}>
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button variant={expanded ? "outline" : "default"} className="rounded-full px-6 h-11"
              onClick={() => setExpanded((v) => !v)} data-testid="faq-toggle-all">
              {expanded ? "Show fewer questions" : "View all FAQs"}
            </Button>
            <Button variant="ghost" className="rounded-full gap-1.5" onClick={onCta} data-testid="faq-talk-to-us">
              Still unsure? Open an account <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="relative lg:sticky lg:top-24">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#3E2E65]/[0.06] to-[#A7C63F]/[0.10]" />
          <img src={FAQ_IMAGE} alt="Tharwy support" loading="lazy" data-testid="faq-illustration"
            className="relative w-full rounded-[1.75rem] object-cover aspect-square shadow-[0_24px_60px_-30px_rgba(26,76,49,0.45)]" />
        </div>
      </div>
    </section>
  );
};
